import React, {createContext, useContext, useEffect, useState} from 'react';
import axios from 'axios';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [authToken, setAuthToken] = useState(localStorage.getItem('access_token'));
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem('auth_user');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            localStorage.removeItem('auth_user');
            return null;
        }
    });
    const [customer, setCustomer] = useState(() => {
        try {
            const raw = localStorage.getItem('auth_customer');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            localStorage.removeItem('auth_customer');
            return null;
        }
    });
    const [impersonator, setImpersonator] = useState(() => {
        try {
            const raw = localStorage.getItem('auth_impersonator');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            localStorage.removeItem('auth_impersonator');
            return null;
        }
    });
    const [isImpersonating, setIsImpersonating] = useState(
        () => localStorage.getItem('auth_impersonating') === 'true'
    );

    const login = async (data) => {
        await localStorage.setItem('access_token', data.access_token);
        await localStorage.setItem('auth_user', JSON.stringify(data.user ?? null));
        await localStorage.setItem('auth_customer', JSON.stringify(data.customer ?? null));
        const impersonatorData = data?.impersonation ? (data.impersonator ?? null) : null;
        await localStorage.setItem('auth_impersonator', JSON.stringify(impersonatorData));
        await localStorage.setItem('auth_impersonating', data?.impersonation ? 'true' : 'false');
        await setUser(data.user ?? null);
        await setCustomer(data.customer ?? null);
        await setImpersonator(impersonatorData);
        await setIsImpersonating(!!data?.impersonation);
        await setAuthToken(data.access_token);
    };

    const logout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_customer');
        localStorage.removeItem('auth_impersonator');
        localStorage.removeItem('auth_impersonating');
        setUser(null);
        setCustomer(null);
        setImpersonator(null);
        setIsImpersonating(false);
        setAuthToken(null);
    };

    useEffect(() => {
        const hydrate = async () => {
            if (!authToken || user || customer) return;
            try {
                const client = axios.create({
                    baseURL: process.env.REACT_APP_API_URL,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const { data } = await client.get('/auth/me');
                localStorage.setItem('auth_user', JSON.stringify(data?.user ?? null));
                localStorage.setItem('auth_customer', JSON.stringify(data?.customer ?? null));
                setUser(data?.user ?? null);
                setCustomer(data?.customer ?? null);
            } catch (e) {
                // Keep token; user might not have access or backend is down.
            }
        };
        hydrate();
    }, [authToken, user, customer]);

    return (
        <AuthContext.Provider
            value={{
                authToken,
                user,
                customer,
                impersonator,
                isImpersonating,
                login,
                logout,
                setUser,
                setCustomer,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
